// Constants
export const STATUS_MAP: Record<string, string> = {
  'in_use': 'Đang sử dụng',
  'in_storage': 'Lưu kho',
  'under_maintenance': 'Đang bảo dưỡng',
  'disposed': 'Đã thanh lý',
  'lost': 'Mất',
  'damaged': 'Hỏng',
  'pending_deployment': 'Chờ triển khai',
  'pending': 'Chờ duyệt',
  'approved': 'Duyệt',
  'completed': 'Hoàn thành'
};

export const TECHNICAL_CONDITION_MAP: Record<string, string> = {
  'new': 'Mới',
  'good': 'Tốt',
  'fair': 'Khá',
  'poor': 'Kém',
  'bad': 'Hỏng'
};

export const SNAPSHOT_FIELD_LABELS: Record<string, string> = {
  asset_code: 'Mã tài sản',
  name: 'Tên tài sản',
  status: 'Tình trạng',
  technical_condition: 'Tình trạng kỹ thuật',
  location_id: 'Vị trí',
  purchase_date: 'Ngày mua',
  purchase_cost: 'Giá mua',
  warranty_period_months: 'Bảo hành (tháng)',
  warranty_expiry_date: 'Ngày hết bảo hành',
  last_audit_date: 'Ngày kiểm kê gần nhất',
  audit_cycle_months: 'Chu kỳ kiểm kê (tháng)',
  supplier_id: 'Nhà cung cấp',
  assigned_to_employee_id: 'Nhân viên sử dụng',
  company_id: 'Công ty',
  asset_approval_status: 'Trạng thái phê duyệt',
  approved_by_user_id: 'Người phê duyệt',
  approval_date: 'Ngày phê duyệt',
  approver_notes: 'Ghi chú phê duyệt',
  parent_asset_id: 'Tài sản cha',
  category_id: 'Danh mục',
  description: 'Mô tả',
  technical_specifications: 'Thông số kỹ thuật',
};

// Helper functions
export const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatVND = (value: number | undefined): string => {
  if (!value) return '—';
  const roundedValue = Math.round(value);
  return `${roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} VNĐ`;
};

export const mapStatusToVN = (status: string): string => {
  return STATUS_MAP[status] || status;
};

export const mapTechnicalConditionToVN = (condition: string): string => {
  return TECHNICAL_CONDITION_MAP[condition] || condition;
};

export const getFieldLabel = (key: string) => SNAPSHOT_FIELD_LABELS[key] || key;

// Helper function để map đơn vị sang tiếng Việt
export const mapUnitToVN = (unit: string | undefined): string => {
  if (!unit || unit.trim() === '') return '';
  
  const unitMap: Record<string, string> = {
    // Đơn vị đếm phổ biến
    'ci': 'cái',
    'piece': 'cái',
    'pcs': 'cái',
    'pc': 'cái',
    'unit': 'đơn vị',
    'item': 'món',
    'thing': 'cái',
    'object': 'vật',
    

    
    // Đơn vị khối lượng
    'kg': 'kg',
    'g': 'g',
    'gram': 'g',
    'ton': 'tấn',
    'lb': 'pound',
    'pound': 'pound',
    'oz': 'ounce',
    'ounce': 'ounce',
    
    // Đơn vị thể tích
    'l': 'lít',
    'liter': 'lít',
    'ml': 'ml',
    'gallon': 'gallon',
    
    // Đơn vị độ dài
    'm': 'mét',
    'meter': 'mét',
    'cm': 'cm',
    'mm': 'mm',
    'foot': 'feet',
    'inch': 'inch',
    'yard': 'yard',
    
    // Đơn vị đóng gói
    'box': 'hộp',
    'pack': 'gói',
    'set': 'bộ',
    'pair': 'đôi',
    'dozen': 'tá',
    'roll': 'cuộn',
    'sheet': 'tờ',
    'bottle': 'chai',
    'can': 'lon',
    'bag': 'túi',
    'container': 'thùng',
    'carton': 'thùng',
    'pallet': 'pallet',
    
    // Đơn vị diện tích/thể tích
    'meter2': 'm²',
    'm2': 'm²',
    'meter3': 'm³',
    'm3': 'm³',
    
    // Đơn vị thời gian
    'hour': 'giờ',
    'minute': 'phút',
    'day': 'ngày',
    'week': 'tuần',
    'month': 'tháng',
    'year': 'năm'
  };
  
  const normalizedUnit = unit.toLowerCase().trim();
  return unitMap[normalizedUnit] || unit;
};

// Helper function để parse snapshot
export const parseSnapshot = (snapshot: any): any => {
  if (!snapshot) return {};
  
  if (typeof snapshot === 'string') {
    try {
      return JSON.parse(snapshot);
    } catch {
      return {};
    }
  }
  
  if (typeof snapshot === 'object') {
    return snapshot;
  }
  
  return {};
};

// Helper function để format giá trị dựa trên key
export const formatFieldValue = (
  key: string, 
  value: any, 
  mappingFunctions: {
    getCompanyName: (id: number | undefined | null) => string;
    getCategoryName: (id: number | undefined | null) => string;
    getSupplierName: (id: number | undefined | null) => string;
    getEmployeeName: (id: number | undefined | null) => string;
    getLocationName: (id: number | undefined | null) => string;
  }
): string => {
  if (value === null || value === undefined || value === '') return '—';
  
  // Xử lý các trường ngày
  if (key.includes('date') || key.includes('Date')) {
    return formatDate(value);
  }
  
  // Xử lý các trường ID cần map
  switch (key) {
    case 'company_id':
      return mappingFunctions.getCompanyName(value);
    case 'category_id':
      return mappingFunctions.getCategoryName(value);
    case 'supplier_id':
      return mappingFunctions.getSupplierName(value);
    case 'assigned_to_employee_id':
      return mappingFunctions.getEmployeeName(value);
    case 'location_id':
      return mappingFunctions.getLocationName(value);
    case 'purchase_price':
    case 'purchase_cost':
      return formatVND(value);
    case 'warranty_months':
    case 'audit_cycle_months':
      return value ? String(value) : '—';
    case 'technical_specifications':
      if (typeof value === 'object') {
        return 'Thông số kỹ thuật'; // Placeholder, sẽ được xử lý riêng
      }
      return String(value);
    case 'status':
      return mapStatusToVN(String(value));
    case 'technical_condition':
      return mapTechnicalConditionToVN(String(value));
    default:
      return String(value);
  }
};

// Helper function để kiểm tra xem có phải kiểm kê đầu tiên không
export const isFirstAudit = (auditId: number, auditHistory: any[]): boolean => {
  return auditHistory.length === 1 || auditId === Math.min(...auditHistory.map(a => a.id));
};

// Helper function để lấy kiểm kê trước đó
export const getPreviousAudit = (currentId: number, auditHistory: any[]): any => {
  return auditHistory
    .filter((a: any) => a.id < currentId)
    .sort((a: any, b: any) => b.id - a.id)[0];
};

// Helper function để kiểm tra thay đổi field
export const hasFieldChanged = (fieldName: string, currentSnapshot: any, previousSnapshot: any): boolean => {
  if (!previousSnapshot) return false;
  return previousSnapshot[fieldName] !== currentSnapshot[fieldName];
};

// Helper function để lấy tên location
export const getLocationName = (locationId: number | undefined | null, locations: Location[]): string => {
  if (!locationId) return '—';
  return locations.find(l => l.id === locationId)?.name || '—';
};

// Types
export interface AuditRecord {
  id: number;
  audit_date: string;
  result: string;
  status: string;
  technical_condition: string;
  location_id: number;
  notes?: string;
  proposal?: string;
  type?: string;
  audit_type?: string;
  accessories_snapshot?: AccessoryItem[];
  snapshot?: string;
  audit_document_code?: string;
}

export interface AccessoryItem {
  name: string;
  quantity: number;
  unit: string;
  price?: number;
  total_price?: number;
  purchase_date?: string;
  purchase_document?: string;
  notes?: string;
}

export interface Location {
  id: number;
  name: string;
}

export interface Company {
  id: number;
  name: string;
}

export interface Category {
  id: number;
  name: string;
}

export interface Supplier {
  id: number;
  name: string;
}

export interface Employee {
  id: number;
  full_name: string;
} 